import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from '../../../shared/DatePicker'
import TextInput from '../../../shared/TextInput'
import DropdownField from '../../../shared/DropdownField'
import CustomButton from '../../../shared/CustomButton'
import moment from 'moment'
import { getDepartmentList, getPractitionerList } from '../../../services/ApiMethods'
import { useDispatch, useSelector } from 'react-redux'
import { getPayersList } from '../../../redux/slices/VisitsSlice'
import { fetchData } from '../../../helpers/fetchData'

const ApprovalManagementFilter = ({ payload, setPayload, onSearch, loading, showBatchStatus = false, batchStatusOptions = [] }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [departments, setDepartments] = useState([])
    const [practitioners, setPractitioners] = useState([])
    const [policies, setPolicies] = useState([])
    const [fieldLoading, setFieldLoading] = useState(false)

    const { payersData } = useSelector((state) => state?.visits);

    useEffect(() => {
        getDepartmentList(setFieldLoading, setDepartments)
        getPractitionerList(setFieldLoading, setPractitioners)
        dispatch(getPayersList())
    }, [])

    const handleChange = (e) => {
        const { name, value } = e.target || {}

        if (name === 'payer_id') {
            if (value) {
                fetchPayerPolicies(value)
            } else {
                setPolicies([])
            }
            setPayload(prev => ({
                ...prev,
                [name]: value,
                policy_number: ''
            }))
        } else {
            setPayload(prev => ({ ...prev, [name]: value }))
        }
    }

    const fetchPayerPolicies = async (payerId) => {
        const response = await fetchData('POST', '/v3/insurancePolicy/getByPayerId', { payer_id: payerId });
        response?.data ? setPolicies(response.data) : setPolicies([])
    }

    const handleDateChange = (e) => {
        const { name, value } = e.target || {}
        setPayload(prev => ({ ...prev, [name]: moment(value).format('YYYY-MM-DD') }))
    }

    const handleReset = () => {
        const initialPayload = {
            "page": 1,
            "perPage": 20,
            "start_date": moment().format("YYYY-MM-DD"),
            "end_date": moment().format("YYYY-MM-DD"),
            "practitioner_id": "",
            "department_id": "",
            "patient_file_number": "",
            "patient_name": "",
            "patient_document_id": "",
            "payer_id": "",
            "policy_number": "",
            "batch_status": ""
        }
        setPayload(initialPayload)
        setPolicies([])
        onSearch(initialPayload)
    }

    return (
        <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4 items-end">
            <div className="w-[180px]">
                <DatePicker
                    name="start_date"
                    placeholder={t('StartDate')}
                    value={payload.start_date ? moment(payload.start_date).toDate() : null}
                    handleChange={handleDateChange}
                    useFormik={false}
                    className="!mb-0"
                />
            </div>
            <div className="w-[180px]">
                <DatePicker
                    name="end_date"
                    placeholder={t('EndDate')}
                    value={payload.end_date ? moment(payload.end_date).toDate() : null}
                    handleChange={handleDateChange}
                    useFormik={false}
                    className="!mb-0"
                />
            </div>
            <div className="w-[200px]">
                <DropdownField
                    name="department_id"
                    options={departments?.map(d => ({ label: d.name_en, value: d.id }))}
                    value={payload.department_id}
                    handleChange={handleChange}
                    useFormik={false}
                    filter
                    showClear
                    className="!mb-0"
                    placeholder={t('SelectDepartment')}
                />
            </div>
            <div className="w-[200px]">
                <DropdownField
                    name="practitioner_id"
                    options={practitioners?.map(p => ({ label: p.name, value: p.practitioner_id }))}
                    value={payload.practitioner_id}
                    handleChange={handleChange}
                    useFormik={false}
                    filter
                    showClear
                    className="!mb-0"
                    placeholder={t('SelectPractitioner')}
                />
            </div>

            <div className="w-[200px]">
                <DropdownField
                    name="payer_id"
                    options={payersData?.data?.map(item => ({
                        label: `${item?.name} (${item?.category})`,
                        value: item?.payer_id,
                    }))}
                    value={payload.payer_id}
                    handleChange={handleChange}
                    useFormik={false}
                    filter
                    showClear
                    className="!mb-0"
                    placeholder={t('Select Payer')}
                />
            </div>

            <div className="w-[200px]">
                <DropdownField
                    name="policy_number"
                    options={policies?.map(item => ({
                        label: `${item?.policy_holder_name} (${item?.policy_number})`,
                        value: item?.policy_number,
                    }))}
                    value={payload.policy_number}
                    handleChange={handleChange}
                    useFormik={false}
                    filter
                    showClear
                    disabled={!payload.payer_id}
                    className="!mb-0"
                    placeholder={t('Select Policy')}
                />
            </div>

            {showBatchStatus && (
                <div className="w-[200px]">
                    <DropdownField
                        name="batch_status"
                        options={batchStatusOptions}
                        value={payload.batch_status}
                        handleChange={handleChange}
                        useFormik={false}
                        filter
                        showClear
                        className="!mb-0"
                        placeholder={t('Select Batch Status')}
                    />
                </div>
            )}

            <div className="w-[180px]">
                <TextInput
                    name="patient_name"
                    value={payload.patient_name}
                    handleChange={handleChange}
                    useFormik={false}
                    parentClass="!mb-0"
                    placeholder={t('PatientName')}
                />
            </div>
            <div className="w-[180px]">
                <TextInput
                    name="patient_file_number"
                    value={payload.patient_file_number}
                    handleChange={handleChange}
                    useFormik={false}
                    parentClass="!mb-0"
                    placeholder={t('MRN')}
                />
            </div>
            <div className="w-[180px]">
                <TextInput
                    name="patient_document_id"
                    value={payload.patient_document_id}
                    handleChange={handleChange}
                    useFormik={false}
                    parentClass="!mb-0"
                    placeholder={t('DocumentID')}
                />
            </div>
            <div className="flex gap-2">
                <CustomButton
                    title={t('Search')}
                    icon="pi pi-search"
                    onClick={() => onSearch(payload)}
                    loading={loading}
                    className="h-[35px] w-fit"
                    buttonType="primary"
                />
                <CustomButton
                    title={t('Reset')}
                    icon="pi pi-sync"
                    buttonType="reset"
                    onClick={handleReset}
                    disabled={loading}
                    className="h-[35px] w-fit"
                />
            </div>
        </div>
    )
}

export default ApprovalManagementFilter
